/**
 * My Account Page
 * 
 * Admin user profile page displaying account information.
 * 
 * @version 1.0.0
 */

import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { AccountCircle, Email, Phone, Business, Edit, Visibility, VisibilityOff } from '@mui/icons-material';
import logger from '../logger';

const MyAccountPage = () => {
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [passwordData, setPasswordData] = useState({
    current: '',
    new: '',
    confirm: '',
  });
  const [passwordError, setPasswordError] = useState('');
  const [formData, setFormData] = useState({
    name: 'Super Admin',
    email: 'admin@rootswings.com',
    phone: '+1 (555) 123-4567',
    organization: 'Roots & Wings NGO',
    role: 'Super Administrator',
    joinDate: '2024-01-15',
  });

  const [editData, setEditData] = useState(formData);

  useEffect(() => {
    logger.info('My Account page loaded');
  }, []);

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditData({
      ...editData,
      [name]: value,
    });
  };

  const handleSaveChanges = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 500));

      setFormData(editData);
      setEditing(false);
      setSuccess('Profile updated successfully!');
      logger.info('Profile updated', editData);

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to update profile. Please try again.');
      logger.error('Profile update failed', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setEditData(formData);
    setEditing(false);
  };

  const handleOpenPasswordDialog = () => {
    setPasswordData({ current: '', new: '', confirm: '' });
    setPasswordError('');
    setShowPasswords({ current: false, new: false, confirm: false });
    setOpenPasswordDialog(true);
  };

  const handleClosePasswordDialog = () => {
    setOpenPasswordDialog(false);
    setPasswordData({ current: '', new: '', confirm: '' });
    setPasswordError('');
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData({
      ...passwordData,
      [name]: value,
    });
  };

  const handleTogglePasswordVisibility = (field) => {
    setShowPasswords({
      ...showPasswords,
      [field]: !showPasswords[field],
    });
  };

  const handleChangePassword = async () => {
    setPasswordError('');

    // Validation
    if (!passwordData.current || !passwordData.new || !passwordData.confirm) {
      setPasswordError('All password fields are required.');
      return;
    }

    if (passwordData.new.length < 8) {
      setPasswordError('New password must be at least 8 characters long.');
      return;
    }

    if (passwordData.new !== passwordData.confirm) {
      setPasswordError('New passwords do not match.');
      return;
    }

    if (passwordData.current === passwordData.new) {
      setPasswordError('New password must be different from current password.');
      return;
    }

    setLoading(true);

    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 500));

      setSuccess('Password changed successfully!');
      handleClosePasswordDialog();
      logger.info('Password changed successfully');

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setPasswordError('Failed to change password. Please try again.');
      logger.error('Password change failed', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth={false} sx={{ maxWidth: '100%', py: 4 }}>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1a3a2a', mb: 1 }}>
          My Account
        </Typography>
        <Typography variant="body2" sx={{ color: '#666' }}>
          Manage your admin profile and account settings
        </Typography>
      </Box>

      {/* Alerts */}
      {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* Profile Card */}
      <Paper sx={{ p: 4, mb: 3, borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        {/* Avatar Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, pb: 3, borderBottom: '1px solid #eee' }}>
          <Avatar
            sx={{
              width: 100,
              height: 100,
              backgroundColor: '#8b3a3a',
              fontSize: '2.5rem',
              fontWeight: 'bold',
              mr: 3,
            }}
          >
            SA
          </Avatar>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1a3a2a' }}>
              {formData.name}
            </Typography>
            <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
              {formData.role}
            </Typography>
            <Typography variant="caption" sx={{ color: '#999', display: 'block', mt: 0.5 }}>
              Member since {new Date(formData.joinDate).toLocaleDateString()}
            </Typography>
          </Box>
        </Box>

        {/* Profile Information */}
        {!editing ? (
          <>
            {/* Read-only View */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ color: '#999', textTransform: 'uppercase', fontWeight: 'bold' }}>
                    Full Name
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#333', fontWeight: '500', mt: 0.5 }}>
                    {formData.name}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ color: '#999', textTransform: 'uppercase', fontWeight: 'bold' }}>
                    <Email sx={{ fontSize: '1rem', mr: 0.5, verticalAlign: 'middle' }} />
                    Email
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#333', fontWeight: '500', mt: 0.5 }}>
                    {formData.email}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ color: '#999', textTransform: 'uppercase', fontWeight: 'bold' }}>
                    <Phone sx={{ fontSize: '1rem', mr: 0.5, verticalAlign: 'middle' }} />
                    Phone
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#333', fontWeight: '500', mt: 0.5 }}>
                    {formData.phone}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ color: '#999', textTransform: 'uppercase', fontWeight: 'bold' }}>
                    <Business sx={{ fontSize: '1rem', mr: 0.5, verticalAlign: 'middle' }} />
                    Organization
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#333', fontWeight: '500', mt: 0.5 }}>
                    {formData.organization}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ color: '#999', textTransform: 'uppercase', fontWeight: 'bold' }}>
                    <AccountCircle sx={{ fontSize: '1rem', mr: 0.5, verticalAlign: 'middle' }} />
                    Role
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#333', fontWeight: '500', mt: 0.5 }}>
                    {formData.role}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {/* Edit Button */}
            <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #eee', display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                startIcon={<Edit />}
                variant="contained"
                sx={{
                  backgroundColor: '#2c5f2d',
                  color: 'white',
                  textTransform: 'none',
                  fontSize: '0.95rem',
                  px: 3,
                  '&:hover': {
                    backgroundColor: '#1f4620',
                  },
                }}
                onClick={() => {
                  setEditData(formData);
                  setEditing(true);
                }}
              >
                Edit Profile
              </Button>
            </Box>
          </>
        ) : (
          <>
            {/* Edit Mode */}
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Full Name"
                  name="name"
                  value={editData.name}
                  onChange={handleEditChange}
                  variant="outlined"
                  size="small"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={editData.email}
                  onChange={handleEditChange}
                  variant="outlined"
                  size="small"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  name="phone"
                  value={editData.phone}
                  onChange={handleEditChange}
                  variant="outlined"
                  size="small"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Organization"
                  name="organization"
                  value={editData.organization}
                  onChange={handleEditChange}
                  variant="outlined"
                  size="small"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Role"
                  name="role"
                  value={editData.role}
                  onChange={handleEditChange}
                  variant="outlined"
                  size="small"
                  disabled
                />
              </Grid>
            </Grid>

            {/* Action Buttons */}
            <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #eee', display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                variant="outlined"
                sx={{
                  color: '#666',
                  borderColor: '#ddd',
                  textTransform: 'none',
                  fontSize: '0.95rem',
                  px: 3,
                }}
                onClick={handleCancel}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                sx={{
                  backgroundColor: '#2c5f2d',
                  color: 'white',
                  textTransform: 'none',
                  fontSize: '0.95rem',
                  px: 3,
                  '&:hover': {
                    backgroundColor: '#1f4620',
                  },
                }}
                onClick={handleSaveChanges}
                disabled={loading}
              >
                {loading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
                Save Changes
              </Button>
            </Box>
          </>
        )}
      </Paper>

      {/* Account Settings Section */}
      <Paper sx={{ p: 4, borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1a3a2a', mb: 2 }}>
          Account Settings
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Box>
          <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
            <strong>Password</strong>
          </Typography>
          <Typography variant="caption" sx={{ color: '#999', display: 'block', mb: 2 }}>
            Last changed: 180 days ago. We recommend updating your password regularly.
          </Typography>
          <Button
            variant="contained"
            sx={{
              backgroundColor: '#2c5f2d',
              color: 'white',
              textTransform: 'none',
              fontSize: '0.9rem',
              '&:hover': {
                backgroundColor: '#1f4620',
              },
            }}
            onClick={handleOpenPasswordDialog}
          >
            Change Password
          </Button>
        </Box>
      </Paper>

      {/* Change Password Dialog */}
      <Dialog
        open={openPasswordDialog}
        onClose={handleClosePasswordDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '8px',
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 'bold',
            color: '#1a3a2a',
            paddingBottom: 1,
          }}
        >
          Change Password
        </DialogTitle>
        <DialogContent>
          {passwordError && (
            <Alert severity="error" sx={{ mb: 2, mt: 2 }}>
              {passwordError}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Current Password"
            name="current"
            type={showPasswords.current ? 'text' : 'password'}
            value={passwordData.current}
            onChange={handlePasswordChange}
            variant="outlined"
            size="small"
            sx={{ mb: 2, mt: 2 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => handleTogglePasswordVisibility('current')}
                    edge="end"
                    size="small"
                  >
                    {showPasswords.current ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            label="New Password"
            name="new"
            type={showPasswords.new ? 'text' : 'password'}
            value={passwordData.new}
            onChange={handlePasswordChange}
            variant="outlined"
            size="small"
            sx={{ mb: 2 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => handleTogglePasswordVisibility('new')}
                    edge="end"
                    size="small"
                  >
                    {showPasswords.new ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            label="Confirm New Password"
            name="confirm"
            type={showPasswords.confirm ? 'text' : 'password'}
            value={passwordData.confirm}
            onChange={handlePasswordChange}
            variant="outlined"
            size="small"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => handleTogglePasswordVisibility('confirm')}
                    edge="end"
                    size="small"
                  >
                    {showPasswords.confirm ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Typography variant="caption" sx={{ color: '#999', display: 'block', mt: 2 }}>
            Password must be at least 8 characters long.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleClosePasswordDialog}
            sx={{
              color: '#666',
              textTransform: 'none',
              fontSize: '0.95rem',
            }}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleChangePassword}
            variant="contained"
            sx={{
              backgroundColor: '#2c5f2d',
              color: 'white',
              textTransform: 'none',
              fontSize: '0.95rem',
              '&:hover': {
                backgroundColor: '#1f4620',
              },
            }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
            Change Password
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MyAccountPage;
